// A small color picker, reachable from the pre-join screen. Breeze carries
// no brand look of its own (BRIEF "Answers, 2026-09-27"), so the only
// look-related choice left is which of packages/core's ten accent hues this
// device uses — the same ten seamless itself offers. A row opens a sheet of
// circular swatches, each named for a screen reader (aria-label), and the
// choice persists via settingsStore.ts.

import { useState, type JSX } from "react";
import { ACCENT_HUES, ACCENT_HUE_KEYS, type AccentHue } from "@breeze/core";
import { clearStoredAccent, getStoredAccent, hasHouseAccent, hasStoredAccent, setStoredAccent } from "../settingsStore";

const HUE_NAMES: Record<AccentHue, string> = {
  blue: "Blue",
  indigo: "Indigo",
  violet: "Violet",
  pink: "Pink",
  red: "Red",
  orange: "Orange",
  amber: "Amber",
  green: "Green",
  teal: "Teal",
  cyan: "Cyan",
};

/** "house" is the page's own accent (worker/src/look.ts): Adam's, until this
 * device picks one of the ten. */
type Choice = AccentHue | "house";

function initialChoice(): Choice {
  return !hasStoredAccent() && hasHouseAccent() ? "house" : getStoredAccent();
}

function applyChoice(choice: Choice): void {
  if (choice === "house") {
    clearStoredAccent();
    document.documentElement.style.removeProperty("--accent");
  } else {
    setStoredAccent(choice);
    document.documentElement.style.setProperty("--accent", ACCENT_HUES[choice]);
  }
}

function swatchColor(choice: Choice): string {
  return choice === "house" ? "var(--house-accent)" : ACCENT_HUES[choice];
}

function choiceName(choice: Choice): string {
  return choice === "house" ? "Default" : HUE_NAMES[choice];
}

function choices(): Choice[] {
  return hasHouseAccent() ? ["house", ...ACCENT_HUE_KEYS] : [...ACCENT_HUE_KEYS];
}

interface AccentPickerProps {
  readonly onChange?: (hue: AccentHue | null) => void;
}

export function AccentPicker({ onChange }: AccentPickerProps): JSX.Element {
  const [accent, setAccent] = useState<Choice>(initialChoice);
  const [open, setOpen] = useState(false);

  const choose = (choice: Choice): void => {
    setAccent(choice);
    applyChoice(choice);
    onChange?.(choice === "house" ? null : choice);
    setOpen(false);
  };

  return (
    <>
      <button type="button" className="row" onClick={() => setOpen(true)}>
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: swatchColor(accent),
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1 }}>Color</span>
        <span style={{ color: "var(--text-2)", fontSize: 14 }}>{choiceName(accent)}</span>
      </button>

      {open ? (
        <div className="scrim" onClick={() => setOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-label="Choose a color"
            onClick={(event) => event.stopPropagation()}
            style={{ padding: "var(--space-5) var(--space-4)" }}
          >
            <h2 style={{ margin: "0 0 var(--space-4)", fontSize: 17, fontWeight: 600 }}>Color</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "var(--space-3)",
                justifyItems: "center",
              }}
            >
              {choices().map((hue) => (
                <button
                  key={hue}
                  type="button"
                  onClick={() => choose(hue)}
                  aria-label={choiceName(hue)}
                  aria-pressed={hue === accent}
                  style={{
                    width: 44,
                    height: 44,
                    minWidth: "var(--tap-min)",
                    minHeight: "var(--tap-min)",
                    borderRadius: "50%",
                    background: swatchColor(hue),
                    border: hue === accent ? "3px solid var(--text)" : "1px solid var(--line)",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Just the swatches (the default first, when the page has one), for a sheet that has other settings beside color
 * (the join screen's settings). Choosing one paints and remembers it. */
export function AccentSwatches({ onChange }: AccentPickerProps): JSX.Element {
  const [accent, setAccent] = useState<Choice>(initialChoice);
  return (
    <div
      role="radiogroup"
      aria-label="Color"
      style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "var(--space-3)", justifyItems: "center" }}
    >
      {choices().map((hue) => (
        <button
          key={hue}
          type="button"
          role="radio"
          aria-checked={hue === accent}
          aria-label={choiceName(hue)}
          onClick={() => {
            setAccent(hue);
            applyChoice(hue);
            onChange?.(hue === "house" ? null : hue);
          }}
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: swatchColor(hue),
            border: hue === accent ? "3px solid var(--text)" : "1px solid var(--line)",
            cursor: "pointer",
          }}
        />
      ))}
    </div>
  );
}
